import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ClipboardList, Building2, BarChart3, Users, ArrowRight, Smartphone, Menu, X, CheckCircle2, UtensilsCrossed, TrendingUp, Clock, ShieldCheck, Send, Phone, Mail, Building, UserCircle, MessageSquare, Star, Quote, Truck, CreditCard } from 'lucide-react';
import { Footer } from '../components/Footer';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { addDemoRequest } from '../utils/demoRequests';
function AnimatedSection({
  children,
  className,
  delay = 0
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 30
  }} transition={{
    duration: 0.6,
    delay: delay,
    ease: 'easeOut'
  }} className={className} data-id="element-3952">
      {children}
    </motion.div>;
}
const features = [{
  icon: <ClipboardList className="w-6 h-6" data-id="element-3953" />,
  title: 'Pedidos Centralizados',
  description: 'Tus empleados eligen su almuerzo desde cualquier dispositivo. Tú recibes un resumen consolidado sin llamadas ni Excel.'
}, {
  icon: <Smartphone className="w-6 h-6" data-id="element-3954" />,
  title: 'Menú Digital Diario',
  description: 'Menú actualizado cada día con fotos, descripciones y opciones personalizables para cada gusto y restricción alimentaria.'
}, {
  icon: <CreditCard className="w-6 h-6" data-id="element-3955" />,
  title: 'Subsidios Automáticos',
  description: 'Define cuánto subsidia tu empresa por empleado. Cricket calcula el descuento automáticamente en cada pedido.'
}, {
  icon: <Truck className="w-6 h-6" data-id="element-3956" />,
  title: 'Entrega Puntual',
  description: 'Almuerzos etiquetados y entregados a tiempo en tu oficina. Cada pedido llega identificado con el nombre del empleado.'
}, {
  icon: <BarChart3 className="w-6 h-6" data-id="element-3957" />,
  title: 'Reportes de Consumo',
  description: 'Visualiza cuánto gasta tu empresa en alimentación, qué platos prefieren tus empleados y optimiza tu presupuesto.'
}, {
  icon: <Users className="w-6 h-6" data-id="element-3958" />,
  title: 'Multi-Sede',
  description: 'Gestiona varias oficinas o sucursales desde un solo panel. Cada sede con su menú, horarios y reglas de subsidio.'
}];
const steps = [{
  number: '01',
  title: 'Configuración',
  description: 'Registramos tu empresa, configuramos los subsidios y cargamos el menú disponible.'
}, {
  number: '02',
  title: 'Pedido',
  description: 'Tus empleados piden su almuerzo desde la App o Web antes de la hora de corte.'
}, {
  number: '03',
  title: 'Entrega Puntual',
  description: 'Cricket gestiona la producción y logística para entregar los almuerzos listos para comer.'
}];
const stats = [{
  value: '—',
  label: 'Empresas activas',
  icon: <Building2 className="w-5 h-5" data-id="element-3959" />
}, {
  value: '—',
  label: 'Pedidos mensuales',
  icon: <TrendingUp className="w-5 h-5" data-id="element-3960" />
}, {
  value: '—',
  label: 'Uptime garantizado',
  icon: <ShieldCheck className="w-5 h-5" data-id="element-3961" />
}, {
  value: '—',
  label: 'Tiempo de setup',
  icon: <Clock className="w-5 h-5" data-id="element-3962" />
}];
const testimonials = [{
  quote: '—',
  name: '—',
  role: '—',
  company: '—'
}, {
  quote: '—',
  name: '—',
  role: '—',
  company: '—'
}, {
  quote: '—',
  name: '—',
  role: '—',
  company: '—'
}];
export function LandingPage() {
  useDocumentTitle('Inicio');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [demoForm, setDemoForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    employees: '',
    message: ''
  });
  const [demoSubmitted, setDemoSubmitted] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const handleDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDemoLoading(true);
    try {
      // Guardar en Supabase (o localStorage como fallback)
      await addDemoRequest(demoForm);
      // ── Enviar formulario via Web3Forms (gratis, sin backend) ──
      const WEB3FORMS_KEY = 'TU_ACCESS_KEY_AQUI';
      try {
        const response = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            access_key: WEB3FORMS_KEY,
            subject: `🍽️ Nueva solicitud de Demo - ${demoForm.company}`,
            from_name: 'Cricket - Landing Page',
            name: demoForm.name,
            email: demoForm.email,
            empresa: demoForm.company,
            telefono: demoForm.phone,
            cantidad_empleados: demoForm.employees,
            mensaje: demoForm.message || 'Sin mensaje adicional'
          })
        });
        const result = await response.json();
        if (!result.success) {
          console.error('Web3Forms error:', result);
        }
      } catch (webError) {
        console.error('Web3Forms fetch error:', webError);
      }
      setDemoSubmitted(true);
    } catch (error) {
      console.error('Error enviando formulario:', error);
      setDemoSubmitted(true);
    } finally {
      setDemoLoading(false);
    }
  };
  return <div className="min-h-screen bg-white flex flex-col font-sans text-gray-900 selection:bg-gray-900 selection:text-white scroll-smooth" data-id="element-3963">
      {/* ───── Navbar ───── */}
      <motion.nav initial={{
      y: -100
    }} animate={{
      y: 0
    }} transition={{
      duration: 0.5
    }} className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm transition-all" data-id="element-3964">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between" data-id="element-3965">
          <Link to="/" className="flex items-center gap-2 group" data-id="element-3966">
            <img src="https://i.imgur.com/bxPZvDb.png" alt="Cricket Logo" className="h-9 w-auto object-contain transition-transform group-hover:scale-105" data-id="element-3967" />
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-0.5" data-id="element-3968">
            <a href="#features" className="group relative flex items-center gap-1.5 px-3.5 py-3.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors whitespace-nowrap" data-id="element-3969">
              Características
            </a>
            <a href="#how-it-works" className="group relative flex items-center gap-1.5 px-3.5 py-3.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors whitespace-nowrap" data-id="element-3970">
              Cómo funciona
            </a>
            <a href="#demo" className="group relative flex items-center gap-1.5 px-3.5 py-3.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors whitespace-nowrap" data-id="element-3971">
              Agendar Demo
            </a>
          </div>

          {/* Right side actions */}
          <div className="hidden md:flex items-center gap-1.5" data-id="element-3972">
            {/* Divider */}
            <div className="w-px h-6 bg-gray-200 mx-1" data-id="element-3973" />
            <Link to="/login" className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-all text-sm font-medium" data-id="element-3974">
              Iniciar Sesión
            </Link>
            <motion.a whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} href="#demo" className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition-all shadow-sm" data-id="element-3975">
              Agendar Demo
            </motion.a>
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden p-2 rounded-xl text-gray-500 hover:bg-gray-50 hover:text-gray-900 transition-all" data-id="element-3976">
            {isMobileMenuOpen ? <X className="w-5 h-5" data-id="element-3977" /> : <Menu className="w-5 h-5" data-id="element-3978" />}
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} className="md:hidden border-t border-gray-100 bg-white p-4 flex flex-col gap-1 shadow-lg" data-id="element-3979">
            <a href="#features" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-600 px-3 py-2.5 rounded-xl hover:bg-gray-50 hover:text-gray-900 transition-colors" data-id="element-3980">
              Características
            </a>
            <a href="#how-it-works" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-600 px-3 py-2.5 rounded-xl hover:bg-gray-50 hover:text-gray-900 transition-colors" data-id="element-3981">
              Cómo funciona
            </a>
            <a href="#demo" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-600 px-3 py-2.5 rounded-xl hover:bg-gray-50 hover:text-gray-900 transition-colors" data-id="element-3982">
              Agendar Demo
            </a>
            <div className="h-px bg-gray-100 my-1" data-id="element-3983" />
            <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-600 px-3 py-2.5 rounded-xl hover:bg-gray-50 hover:text-gray-900 transition-colors" data-id="element-3984">
              Iniciar Sesión
            </Link>
            <a href="#demo" onClick={() => setIsMobileMenuOpen(false)} className="w-full text-center py-2.5 px-4 text-sm font-semibold rounded-xl text-white bg-gray-900 hover:bg-gray-800 transition-colors" data-id="element-3985">
              Agendar Demo
            </a>
          </motion.div>}
      </motion.nav>

      {/* ───── Hero ───── */}
      <section className="relative pt-10 pb-16 lg:pt-28 lg:pb-32 overflow-hidden" data-id="element-3986">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_80%_60%_at_top_right,_#f0fdf4_0%,_#f9fafb_50%,_white_100%)]" data-id="element-3987"></div>
        <div className="absolute top-0 right-0 -z-10 w-[300px] lg:w-[600px] h-[300px] lg:h-[600px] bg-emerald-100/40 rounded-full blur-3xl opacity-60 pointer-events-none" data-id="element-3988"></div>
        <div className="absolute bottom-0 left-0 -z-10 w-[200px] lg:w-[400px] h-[200px] lg:h-[400px] bg-gray-100/60 rounded-full blur-3xl pointer-events-none" data-id="element-3989"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3990">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-8 items-center" data-id="element-3991">
            <div className="text-center lg:text-left" data-id="element-3992">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} className="hidden sm:inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gray-200 shadow-sm text-gray-700 text-xs font-bold uppercase tracking-wide mb-6" data-id="element-3993">
                <span className="relative flex h-2 w-2" data-id="element-3994">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-3995"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" data-id="element-3996"></span>
                </span>
                Plataforma Corporativa
              </motion.div>

              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-gray-900 mb-6 leading-[1.08]" data-id="element-3997">
                Almuerzos corporativos, <br className="hidden sm:block" data-id="element-3998" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500" data-id="element-3999">
                  sin complicaciones.
                </span>
              </motion.h1>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="text-lg sm:text-xl text-gray-500 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-4000">
                Olvídate de la logística. Cricket centraliza pedidos, subsidios
                y entregas para que tu equipo disfrute su comida y tú recuperes
                tu tiempo.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 sm:gap-4" data-id="element-4001">
                <motion.a whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} href="#demo" className="w-full sm:w-auto px-7 py-3.5 sm:px-8 sm:py-4 text-base font-bold rounded-xl text-white bg-gray-900 hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group" data-id="element-4002">
                  Agendar Demo Gratis
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-4003" />
                </motion.a>
                <motion.div whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} className="w-full sm:w-auto" data-id="element-4004">
                  <Link to="/login" className="w-full sm:w-auto px-7 py-3.5 sm:px-8 sm:py-4 text-base font-bold rounded-xl text-gray-700 bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all shadow-sm hover:shadow-md flex items-center justify-center" data-id="element-4005">
                    Ya tengo cuenta
                  </Link>
                </motion.div>
              </motion.div>

              <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.5,
              delay: 0.5
            }} className="mt-8 lg:mt-10 flex items-center justify-center lg:justify-start gap-4 text-sm text-gray-500" data-id="element-4006">
                <div className="flex -space-x-2" data-id="element-4007">
                  {['MR', 'CM', 'AP', 'JL'].map((initials, i) => <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-gray-900 flex items-center justify-center text-[10px] font-bold text-white" data-id="element-4008">
                      {initials}
                    </div>)}
                </div>
                <p data-id="element-4009">
                  <span className="font-bold text-gray-900" data-id="element-4010">— empresas</span> ya
                  confían en Cricket
                </p>
              </motion.div>
            </div>

            {/* Mockup - Visible on ALL screen sizes */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.3
          }} className="relative flex justify-center lg:block" data-id="element-4011">
              {/* Background circle - smaller on mobile */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] sm:w-[350px] lg:w-[500px] h-[280px] sm:h-[350px] lg:h-[500px] bg-gradient-to-br from-gray-100 to-gray-50 rounded-full border border-gray-100/50" data-id="element-4012"></div>

              {/* Phone mockup card - responsive sizing */}
              <div className="relative z-10 bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-100 p-4 sm:p-6 w-[260px] sm:w-[300px] lg:max-w-sm mx-auto transform lg:-rotate-2 lg:hover:rotate-0 transition-transform duration-500" data-id="element-4013">
                <div className="flex items-center justify-between mb-4 sm:mb-5" data-id="element-4014">
                  <div data-id="element-4015">
                    <h3 className="font-bold text-base sm:text-lg" data-id="element-4016">
                      Hola, Carlos 👋
                    </h3>
                    <p className="text-[10px] sm:text-xs text-gray-400" data-id="element-4017">
                      Subsidio:{' '}
                      <span className="text-green-600 font-semibold" data-id="element-4018">
                        RD$ 350.00
                      </span>
                    </p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-4019">
                    <UtensilsCrossed className="w-4 h-4 sm:w-5 sm:h-5 text-gray-800" data-id="element-4020" />
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-2.5 sm:p-3 mb-2.5 sm:mb-3 flex gap-2.5 sm:gap-3 border border-gray-100" data-id="element-4021">
                  <div className="w-11 h-11 sm:w-14 sm:h-14 bg-gray-100 rounded-lg flex-shrink-0 flex items-center justify-center text-xl sm:text-2xl" data-id="element-4022">
                    🍗
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-4023">
                    <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate" data-id="element-4024">
                      Pollo al Horno
                    </p>
                    <p className="text-[10px] sm:text-[11px] text-gray-400" data-id="element-4025">
                      Con arroz y ensalada
                    </p>
                    <div className="flex justify-between items-center mt-1 sm:mt-1.5" data-id="element-4026">
                      <span className="text-[10px] sm:text-xs font-bold text-gray-900" data-id="element-4027">
                        RD$ 250.00
                      </span>
                      <span className="px-1.5 sm:px-2 py-0.5 bg-green-100 text-green-700 text-[8px] sm:text-[10px] font-bold rounded-full" data-id="element-4028">
                        Subsidiado
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-2.5 sm:p-3 mb-3 sm:mb-4 flex gap-2.5 sm:gap-3 border border-gray-100 shadow-sm" data-id="element-4029">
                  <div className="w-11 h-11 sm:w-14 sm:h-14 bg-gray-100 rounded-lg flex-shrink-0 flex items-center justify-center text-xl sm:text-2xl" data-id="element-4030">
                    🥩
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-4031">
                    <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate" data-id="element-4032">
                      Res Guisada
                    </p>
                    <p className="text-[10px] sm:text-[11px] text-gray-400" data-id="element-4033">
                      Con habichuelas y plátano
                    </p>
                    <div className="flex justify-between items-center mt-1 sm:mt-1.5" data-id="element-4034">
                      <span className="text-[10px] sm:text-xs font-bold text-gray-900" data-id="element-4035">
                        RD$ 320.00
                      </span>
                      <button className="px-2 sm:px-3 py-0.5 sm:py-1 bg-gray-900 rounded-lg text-white text-[9px] sm:text-[11px] font-bold" data-id="element-4036">
                        Agregar
                      </button>
                    </div>
                  </div>
                </div>

                <div className="pt-3 sm:pt-4 border-t border-gray-100" data-id="element-4037">
                  <div className="flex justify-between mb-2.5 sm:mb-3" data-id="element-4038">
                    <span className="text-xs sm:text-sm text-gray-500" data-id="element-4039">
                      Total
                    </span>
                    <span className="text-xs sm:text-sm font-bold" data-id="element-4040">
                      RD$ 250.00
                    </span>
                  </div>
                  <button className="w-full py-2.5 sm:py-3 bg-gray-900 text-white rounded-xl text-xs sm:text-sm font-bold shadow-lg shadow-gray-900/20" data-id="element-4041">
                    Confirmar Pedido →
                  </button>
                </div>
              </div>

              {/* Floating cards - hidden on small mobile, visible from sm up */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.6,
              duration: 0.5
            }} className="absolute -bottom-4 -right-2 sm:-right-4 bg-white p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 flex items-center gap-2 sm:gap-3 hidden sm:flex" style={{
              animation: 'float 3s ease-in-out infinite'
            }} data-id="element-4042">
                <div className="w-7 h-7 sm:w-9 sm:h-9 bg-green-100 rounded-full flex items-center justify-center text-green-600" data-id="element-4043">
                  <CheckCircle2 className="w-3.5 h-3.5 sm:w-5 sm:h-5" data-id="element-4044" />
                </div>
                <div data-id="element-4045">
                  <p className="text-[9px] sm:text-[10px] text-gray-400 font-medium" data-id="element-4046">
                    Ahora mismo
                  </p>
                  <p className="text-[10px] sm:text-xs font-bold text-gray-900" data-id="element-4047">
                    Pedido Entregado ✓
                  </p>
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.8,
              duration: 0.5
            }} className="absolute -top-2 sm:-top-4 -left-2 sm:-left-4 bg-white p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 flex items-center gap-2 sm:gap-3 hidden sm:flex" style={{
              animation: 'float 3s ease-in-out infinite 1.5s'
            }} data-id="element-4048">
                <div className="w-7 h-7 sm:w-9 sm:h-9 bg-blue-100 rounded-full flex items-center justify-center text-blue-600" data-id="element-4049">
                  <TrendingUp className="w-3.5 h-3.5 sm:w-5 sm:h-5" data-id="element-4050" />
                </div>
                <div data-id="element-4051">
                  <p className="text-[9px] sm:text-[10px] text-gray-400 font-medium" data-id="element-4052">
                    Hoy
                  </p>
                  <p className="text-[10px] sm:text-xs font-bold text-gray-900" data-id="element-4053">
                    127 pedidos
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ───── Stats Strip ───── */}
      <section className="py-12 border-y border-gray-100 bg-gray-50/50" data-id="element-4054">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4055">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-4056">
            {stats.map((stat, i) => <AnimatedSection key={i} delay={i * 0.1} className="text-center" data-id="element-4057">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 text-gray-600 mb-3" data-id="element-4058">
                  {stat.icon}
                </div>
                <p className="text-3xl font-bold text-gray-900 tracking-tight" data-id="element-4059">
                  {stat.value}
                </p>
                <p className="text-sm text-gray-500 mt-1" data-id="element-4060">{stat.label}</p>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* ───── Features ───── */}
      <section id="features" className="py-16 lg:py-24 bg-white relative" data-id="element-4061">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_60%_40%_at_bottom_left,_#f0fdf4_0%,_white_60%)]" data-id="element-4062"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4063">
          <AnimatedSection className="text-center mb-12 lg:mb-16 max-w-3xl mx-auto" data-id="element-4064">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wide mb-4" data-id="element-4065">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-4066"></span>
              Funcionalidades
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6 tracking-tight" data-id="element-4067">
              Todo lo que necesitas para tu comedor
            </h2>
            <p className="text-base sm:text-lg text-gray-500" data-id="element-4068">
              Herramientas específicas para resolver los dolores de cabeza de
              RRHH y Operaciones.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6" data-id="element-4069">
            {features.map((feature, index) => <AnimatedSection key={index} delay={index * 0.08} className="group p-7 rounded-2xl bg-white border border-gray-100 hover:border-emerald-100 shadow-sm hover:shadow-xl hover:shadow-emerald-900/5 hover:-translate-y-1.5 transition-all duration-300 relative overflow-hidden" data-id="element-4070">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/0 to-emerald-50/0 group-hover:from-emerald-50/60 group-hover:to-transparent transition-all duration-300 pointer-events-none" data-id="element-4071" />
                <div className="w-12 h-12 rounded-xl bg-gray-50 text-gray-700 flex items-center justify-center mb-5 group-hover:bg-emerald-600 group-hover:text-white transition-all duration-300 shadow-sm" data-id="element-4072">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2.5" data-id="element-4073">
                  {feature.title}
                </h3>
                <p className="text-gray-500 leading-relaxed text-sm" data-id="element-4074">
                  {feature.description}
                </p>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* ───── Testimonials ───── */}
      <section className="py-16 lg:py-24 bg-gray-50/80 border-y border-gray-100" data-id="element-4075">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4076">
          <AnimatedSection className="text-center mb-12 lg:mb-16" data-id="element-4077">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gray-200 text-gray-600 text-xs font-bold uppercase tracking-wide mb-4 shadow-sm" data-id="element-4078">
              <Star className="w-3 h-3 fill-amber-400 text-amber-400" data-id="element-4079" />
              Testimonios
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight" data-id="element-4080">
              Lo que dicen nuestros clientes
            </h2>
            <p className="text-base sm:text-lg text-gray-500" data-id="element-4081">
              Empresas reales, resultados reales.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6" data-id="element-4082">
            {testimonials.map((t, i) => <AnimatedSection key={i} delay={i * 0.1} className="bg-white rounded-2xl p-7 border border-gray-100 shadow-sm relative flex flex-col" data-id="element-4083">
                <div className="flex gap-0.5 mb-4" data-id="element-4084">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" data-id="element-4085" />)}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 text-[15px] flex-1" data-id="element-4086">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100" data-id="element-4087">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center text-white text-xs font-bold shrink-0" data-id="element-4088">
                    {t.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div data-id="element-4089">
                    <p className="text-sm font-bold text-gray-900" data-id="element-4090">{t.name}</p>
                    <p className="text-xs text-gray-500" data-id="element-4091">
                      {t.role} · {t.company}
                    </p>
                  </div>
                </div>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* ───── How It Works ───── */}
      <section id="how-it-works" className="py-16 lg:py-24 bg-gray-900 text-white relative overflow-hidden" data-id="element-4092">
        <div className="absolute inset-0 opacity-[0.07] bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:48px_48px]" data-id="element-4093"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-4094">
          <AnimatedSection className="flex flex-col md:flex-row md:items-end justify-between mb-12 lg:mb-16 gap-6" data-id="element-4095">
            <div data-id="element-4096">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight" data-id="element-4097">
                Así funciona Cricket
              </h2>
              <p className="text-base sm:text-lg text-gray-400 max-w-xl" data-id="element-4098">
                Implementación rápida. Resultados inmediatos.
              </p>
            </div>
            <a href="#demo" className="inline-flex items-center text-white hover:text-gray-300 font-semibold border-b border-white hover:border-gray-300 pb-1 transition-colors" data-id="element-4099">
              Agendar una demo <ArrowRight className="ml-2 w-4 h-4" data-id="element-4100" />
            </a>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12 relative" data-id="element-4101">
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px bg-gradient-to-r from-gray-700 via-gray-500 to-gray-700 border-t border-dashed border-gray-600" data-id="element-4102"></div>
            {steps.map((step, index) => <AnimatedSection key={index} delay={index * 0.2} className="relative group" data-id="element-4103">
                <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-800 rounded-xl sm:rounded-2xl border border-gray-700 flex items-center justify-center text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8 relative z-10 group-hover:bg-white group-hover:text-black transition-colors duration-300 shadow-lg shadow-black/50" data-id="element-4104">
                  {step.number}
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4" data-id="element-4105">
                  {step.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-400 leading-relaxed" data-id="element-4106">
                  {step.description}
                </p>
              </AnimatedSection>)}
          </div>
        </div>
      </section>

      {/* ───── Demo Form ───── */}
      <section id="demo" className="py-16 lg:py-24 bg-white relative overflow-hidden" data-id="element-4107">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-green-50/50 rounded-full blur-3xl -mr-48 -mt-48 pointer-events-none" data-id="element-4108"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gray-100/50 rounded-full blur-3xl -ml-48 -mb-48 pointer-events-none" data-id="element-4109"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-4110">
          <div className="grid lg:grid-cols-2 gap-16 items-start" data-id="element-4111">
            {/* Left - Info */}
            <AnimatedSection data-id="element-4112">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-bold uppercase tracking-wide mb-6" data-id="element-4113">
                <Phone className="w-3.5 h-3.5" data-id="element-4114" />
                Demo personalizada
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight leading-tight" data-id="element-4115">
                Agenda una demo
                <br data-id="element-4116" />
                <span className="text-gray-400" data-id="element-4117">sin compromiso</span>
              </h2>
              <p className="text-lg text-gray-500 mb-8 leading-relaxed" data-id="element-4118">
                Te mostramos cómo Cricket puede transformar la gestión de
                almuerzos en tu empresa. La demo dura ~20 minutos y es 100%
                personalizada a tu caso.
              </p>

              <div className="space-y-5" data-id="element-4119">
                {[{
                title: 'Demo en vivo de la plataforma',
                desc: 'Verás exactamente cómo funciona con datos de ejemplo.'
              }, {
                title: 'Propuesta personalizada',
                desc: 'Precio adaptado al tamaño de tu empresa y necesidades.'
              }, {
                title: 'Sin compromiso',
                desc: 'Explora Cricket sin presión. Decide cuando estés listo.'
              }].map((item, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="flex items-start gap-4" data-id="element-4120">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-4121">
                      <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-4122" />
                    </div>
                    <div data-id="element-4123">
                      <p className="font-semibold text-gray-900" data-id="element-4124">
                        {item.title}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-4125">{item.desc}</p>
                    </div>
                  </motion.div>)}
              </div>

              <div className="mt-10 p-5 bg-gray-50 rounded-2xl border border-gray-100" data-id="element-4126">
                <p className="text-sm text-gray-500 mb-2" data-id="element-4127">
                  ¿Prefieres contactarnos directamente?
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-4128">
                  <a href="https://wa.me/18091234567" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-green-700 transition-colors" data-id="element-4129">
                    <Phone className="w-4 h-4" data-id="element-4130" /> —
                  </a>
                  <a href="mailto:ventas@cricket.do" className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-blue-700 transition-colors" data-id="element-4131">
                    <Mail className="w-4 h-4" data-id="element-4132" /> —
                  </a>
                </div>
              </div>
            </AnimatedSection>

            {/* Right - Form */}
            <AnimatedSection delay={0.2} className="bg-white rounded-3xl border border-gray-200 shadow-xl p-8 lg:p-10" data-id="element-4133">
              {demoSubmitted ? <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="text-center py-12" data-id="element-4134">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-4135">
                    <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-4136" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-4137">
                    ¡Solicitud enviada!
                  </h3>
                  <p className="text-gray-500 mb-6 max-w-sm mx-auto" data-id="element-4138">
                    Nuestro equipo se pondrá en contacto contigo en las próximas
                    24 horas para coordinar tu demo.
                  </p>
                  <button onClick={() => {
                setDemoSubmitted(false);
                setDemoForm({
                  name: '',
                  email: '',
                  company: '',
                  phone: '',
                  employees: '',
                  message: ''
                });
              }} className="text-sm font-semibold text-gray-900 hover:underline underline-offset-2" data-id="element-4139">
                    Enviar otra solicitud
                  </button>
                </motion.div> : <>
                  <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-4140">
                    Solicita tu demo
                  </h3>
                  <p className="text-sm text-gray-500 mb-6" data-id="element-4141">
                    Completa el formulario y te contactaremos pronto.
                  </p>

                  <form onSubmit={handleDemoSubmit} className="space-y-4" data-id="element-4142">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4143">
                      <div data-id="element-4144">
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-4145">
                          Nombre completo
                        </label>
                        <div className="relative" data-id="element-4146">
                          <UserCircle className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" data-id="element-4147" />
                          <input type="text" required value={demoForm.name} onChange={e => setDemoForm({
                        ...demoForm,
                        name: e.target.value
                      })} className="w-full pl-10 pr-4 h-11 border border-gray-200 rounded-xl bg-gray-50/80 focus:bg-white focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm transition-all placeholder:text-gray-400" placeholder="Juan Pérez" data-id="element-4148" />
                        </div>
                      </div>
                      <div data-id="element-4149">
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-4150">
                          Email corporativo
                        </label>
                        <div className="relative" data-id="element-4151">
                          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" data-id="element-4152" />
                          <input type="email" required value={demoForm.email} onChange={e => setDemoForm({
                        ...demoForm,
                        email: e.target.value
                      })} className="w-full pl-10 pr-4 h-11 border border-gray-200 rounded-xl bg-gray-50/80 focus:bg-white focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm transition-all placeholder:text-gray-400" placeholder="juan@empresa.com" data-id="element-4153" />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4154">
                      <div data-id="element-4155">
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-4156">
                          Empresa
                        </label>
                        <div className="relative" data-id="element-4157">
                          <Building className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" data-id="element-4158" />
                          <input type="text" required value={demoForm.company} onChange={e => setDemoForm({
                        ...demoForm,
                        company: e.target.value
                      })} className="w-full pl-10 pr-4 h-11 border border-gray-200 rounded-xl bg-gray-50/80 focus:bg-white focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm transition-all placeholder:text-gray-400" placeholder="Mi Empresa SRL" data-id="element-4159" />
                        </div>
                      </div>
                      <div data-id="element-4160">
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-4161">
                          WhatsApp / Teléfono
                        </label>
                        <div className="relative" data-id="element-4162">
                          <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" data-id="element-4163" />
                          <input type="tel" required value={demoForm.phone} onChange={e => {
                        // Only allow digits, +, -, (, ), spaces
                        const cleaned = e.target.value.replace(/[^\d+\-() ]/g, '');
                        setDemoForm({
                          ...demoForm,
                          phone: cleaned
                        });
                      }} maxLength={20} pattern="[\d\+\-\(\) ]+" className="w-full pl-10 pr-4 h-11 border border-gray-200 rounded-xl bg-gray-50/80 focus:bg-white focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm transition-all placeholder:text-gray-400" placeholder="809-123-4567" data-id="element-4164" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-4165">
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-4166">
                        Cantidad de empleados
                      </label>
                      <select required value={demoForm.employees} onChange={e => setDemoForm({
                    ...demoForm,
                    employees: e.target.value
                  })} className="w-full px-4 h-11 border border-gray-200 rounded-xl bg-gray-50/80 focus:bg-white focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm transition-all text-gray-700 appearance-none cursor-pointer" data-id="element-4167">
                        <option value="" data-id="element-4168">Selecciona un rango</option>
                        <option value="1-50" data-id="element-4169">1 – 50 empleados</option>
                        <option value="51-100" data-id="element-4170">51 – 100 empleados</option>
                        <option value="101-300" data-id="element-4171">101 – 300 empleados</option>
                        <option value="301-500" data-id="element-4172">301 – 500 empleados</option>
                        <option value="500+" data-id="element-4173">Más de 500 empleados</option>
                      </select>
                    </div>

                    <div data-id="element-4174">
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-4175">
                        Mensaje{' '}
                        <span className="text-gray-400 font-normal" data-id="element-4176">
                          (opcional)
                        </span>
                      </label>
                      <div className="relative" data-id="element-4177">
                        <MessageSquare className="absolute left-3.5 top-3 w-[18px] h-[18px] text-gray-400" data-id="element-4178" />
                        <textarea value={demoForm.message} onChange={e => setDemoForm({
                      ...demoForm,
                      message: e.target.value
                    })} rows={3} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl bg-gray-50/80 focus:bg-white focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm transition-all placeholder:text-gray-400 resize-none" placeholder="Cuéntanos sobre tu necesidad..." data-id="element-4179" />
                      </div>
                    </div>

                    <motion.button whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} type="submit" disabled={demoLoading} className="w-full h-12 bg-gray-900 text-white rounded-xl text-[15px] font-semibold shadow-lg shadow-gray-900/10 hover:bg-gray-800 hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-4180">
                      {demoLoading ? <>
                          <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" data-id="element-4181">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" data-id="element-4182" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-4183" />
                          </svg>
                          Enviando...
                        </> : <>
                          <Send className="w-5 h-5" data-id="element-4184" />
                          Solicitar Demo Gratis
                        </>}
                    </motion.button>

                    <p className="text-xs text-gray-400 text-center mt-3" data-id="element-4185">
                      Al enviar, aceptas nuestra política de privacidad. No
                      spam, lo prometemos.
                    </p>
                  </form>
                </>}
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ───── Final CTA ───── */}
      <section className="py-16 lg:py-24 bg-gray-950 text-white relative overflow-hidden" data-id="element-4186">
        <div className="absolute inset-0 opacity-[0.05] bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear_gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:48px_48px]" data-id="element-4187"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-4188">
          <AnimatedSection data-id="element-4189">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-6 tracking-tight" data-id="element-4190">
              ¿Listo para modernizar el almuerzo?
            </h2>
            <p className="text-base sm:text-xl text-gray-400 mb-8 sm:mb-10 max-w-2xl mx-auto" data-id="element-4191">
              Únete a las empresas que ya confían en Cricket para gestionar la
              alimentación de sus colaboradores.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4" data-id="element-4192">
              <motion.a whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} href="#demo" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold rounded-xl text-gray-900 bg-white hover:bg-gray-100 transition-all shadow-lg hover:-translate-y-1" data-id="element-4193">
                Agendar Demo
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-4194" />
              </motion.a>
              <motion.div whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} data-id="element-4195">
                <Link to="/login" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold rounded-xl text-white border border-gray-700 hover:border-gray-500 hover:bg-white/5 transition-all" data-id="element-4196">
                  Iniciar Sesión
                </Link>
              </motion.div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer data-id="element-4197" />

      <style data-id="element-4198">{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </div>;
}